import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthService } from '@features/auth/data-access/auth.service';
import { firstValueFrom } from 'rxjs';
import { IntegrationProviderSlug } from '../interfaces/integration-provider-slug.enum';
import {
  IIntegrationChannelApiResponse,
  IIntegrationChannelsListApiResponse,
  IIntegrationConnectionApiResponse,
  IIntegrationConnectionUpdateApiResponse,
  IIntegrationConnectionsListApiResponse,
  IIntegrationOAuthStartApiResponse,
} from './interfaces/integration-api.interface';

@Injectable({ providedIn: 'root' })
export class IntegrationsApiService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  public async listConnections(): Promise<IIntegrationConnectionApiResponse[]> {
    const response = await firstValueFrom(
      this.http.get<IIntegrationConnectionsListApiResponse>(`${environment.apiUrl}/integrations`, {
        headers: this.authHeaders(),
      }),
    );
    return response.connections;
  }

  public async startOAuth(provider: IntegrationProviderSlug): Promise<string> {
    const response = await firstValueFrom(
      this.http.post<IIntegrationOAuthStartApiResponse>(
        `${environment.apiUrl}/integrations/${provider}/oauth/start`,
        {},
        { headers: this.authHeaders() },
      ),
    );
    return response.authorizationUrl;
  }

  public async listChannels(
    connectionId: string,
    query?: string,
    cursor?: string,
  ): Promise<IIntegrationChannelsListApiResponse> {
    const params = new URLSearchParams();
    if (query) {
      params.set('query', query);
    }
    if (cursor) {
      params.set('cursor', cursor);
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    return firstValueFrom(
      this.http.get<IIntegrationChannelsListApiResponse>(
        `${environment.apiUrl}/integrations/connections/${connectionId}/channels${suffix}`,
        { headers: this.authHeaders() },
      ),
    );
  }

  public async updateSelectedChannel(
    connectionId: string,
    selectedChannel: IIntegrationChannelApiResponse,
  ): Promise<IIntegrationConnectionApiResponse> {
    const response = await firstValueFrom(
      this.http.patch<IIntegrationConnectionUpdateApiResponse>(
        `${environment.apiUrl}/integrations/connections/${connectionId}`,
        { selectedChannel },
        { headers: this.authHeaders() },
      ),
    );
    return response.connection;
  }

  public async disconnect(connectionId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(`${environment.apiUrl}/integrations/connections/${connectionId}`, {
        headers: this.authHeaders(),
      }),
    );
  }

  private authHeaders(): Record<string, string> {
    const token = this.authService.sessionToken();
    if (!token) {
      throw new Error('Missing session token');
    }
    return { Authorization: `Bearer ${token}` };
  }
}
